import {
	DestroyRef,
	Injectable,
	WritableSignal,
	inject,
	signal,
	OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { getFollowedBuildings } from '@app/auth/state/auth.selectors';
import { clearAuBreadcrumb, setAuBreadcrumb } from '@app/layout/state/layout.actions';
import { IOption } from '@app/shared/interfaces/selectable-options.interfaces';
import { Store } from '@ngrx/store';
import { Observable, filter, firstValueFrom, map } from 'rxjs';
import { clearCurrentBuilding, currentBuilding } from './state/my-building.actions';

@Injectable({
	providedIn: 'root',
})
export class MyBuildingService implements OnDestroy {
	private readonly destroyRef = inject(DestroyRef);
	private readonly store$ = inject(Store);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);

	myBuildings$: Observable<IOption[]> = this.store$.select(getFollowedBuildings);
	buildingName: WritableSignal<string> = signal(null);

	async setCurrentBuildingBreadcrumb(config, building = null) {
		this.store$.dispatch(
			setAuBreadcrumb({
				configuration: building ? [{ label: building }, ...config] : config,
			})
		);
	}

	async getCurrentBuilding() {
		const myBuildings = await firstValueFrom(
			this.myBuildings$.pipe(
				filter(myBuildings => !!myBuildings),
				takeUntilDestroyed(this.destroyRef)
			)
		);

		if (myBuildings) {
			const building = myBuildings.find((building: IOption) =>
				this.router.url.includes(building.id)
			);
			if (building) {
				return building;
			}
		}
	}

	getCurrentBuildingBreadcrumb(buildingId: string) {
		return this.myBuildings$.pipe(
			filter(myBuildings => !!myBuildings),
			map(myBuildings => myBuildings.find(({ id }) => id === buildingId)),
			map(building => building.name)
		);
	}

	initBuilding(callback = null) {
		this.route.url.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async _ => {
			const building = await this.getCurrentBuilding();
			if (building) {
				this.store$.dispatch(
					currentBuilding({
						building: {
							name: building.name,
							locationId: building.id,
							locationType: 'Building',
						},
					})
				);
			} else {
				this.store$.dispatch(clearCurrentBuilding());
			}
			if (callback) {
				callback();
			}
			return;
		});
	}

	clearCurrentBuilding() {
		this.store$.dispatch(clearCurrentBuilding());
	}

	ngOnDestroy() {
		this.store$.dispatch(clearCurrentBuilding());
		this.store$.dispatch(clearAuBreadcrumb());
	}
}
